import * as yup from 'yup';
import { HOSTNAME_MAX, isUsableHostname } from '../../../library/models/clinic';
import { emailField, requiredText, TEXT_MAX, TranslateFn } from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';

/** What the API accepts as a slug. */
const SLUG_PATTERN = /^[a-z0-9-]+$/;

const SLUG_MAX = 50;

/**
 * Mirrors the server's own constraints so the common mistakes are caught before a round
 * trip. It is not the enforcement -- the API validates everything again, and its errors
 * are mapped back onto these same fields.
 *
 * Slug is validated in both modes even though it can only be set at creation: an existing
 * slug always satisfies these rules, so the edit form -- where the field is disabled and
 * simply carries its current value -- passes without a special case.
 *
 * Hostname is optional and blank in every edit -- an existing clinic's domains are managed
 * against their own endpoints rather than through this form. It is a schema field regardless
 * of mode for a second reason: `useApiFormError` takes its known-field list from
 * `Object.keys(schema.fields)`, so naming it here is what would let a server error about the
 * hostname land on the hostname field rather than in the form-level alert.
 *
 * Sender email is the reverse: only shown while editing, and blank on create. It is named here
 * for the same `useApiFormError` reason, since the API refuses a malformed address with a
 * `validationErrors.senderEmail` that should land on the field.
 */
export const createClinicSchema = (translate: TranslateFn) => yup.object({
    slug: requiredText(translate, ResourcesIds.CLINIC_SLUG_REQUIRED, SLUG_MAX)
        .matches(SLUG_PATTERN, translate(ResourcesIds.CLINIC_SLUG_INVALID)),
    displayName: requiredText(translate, ResourcesIds.CLINIC_DISPLAY_NAME_REQUIRED, TEXT_MAX),
    hostname: yup.string().defined()
        .max(HOSTNAME_MAX, translate(ResourcesIds.CLINIC_FIELD_TOO_LONG, { max: HOSTNAME_MAX }))
        .test('hostname', translate(ResourcesIds.CLINIC_DOMAIN_INVALID),
            (value) => !value?.trim() || isUsableHostname(value)),
    senderEmail: emailField(translate, { required: false }),
});
